import { Flex, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { DocumentRefreshStatusEnum } from "~/graphql/supergraphEnums";
import { getTimeSinceInMinutes, getTimeSinceInHours, isSameDay } from "~/utils/datetime";
import { formatCountText } from "~/utils/strings";
import { RefreshStatus } from "../RefreshStatusCellRenderer/types";
import {
  tooltipText,
  expiredContainer,
  expiredIconContainer,
  expiresInContainer,
  tooltipTableStyle,
  tooltipLabelStyle,
} from "../styles_vanilla.css";
import { formatDaysAhead } from "../utils";

/**
 * Returns the number of whole calendar days between two dates, regardless of time-of-day.
 * Both dates are truncated to local midnight before differencing, so a date that is
 * "yesterday" always returns 1 even if the timestamps are only a few hours apart.
 */
export const getDaysDifference = (date1: Date, date2: Date): number => {
  const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
  const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());
  return Math.round(Math.abs(d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24));
};

type StatusType = RefreshStatus | DocumentRefreshStatusEnum;

export type BaseStatusTooltipProps = {
  statusType: StatusType;
  tooltipData?: {
    nextRefreshDueDate?: Date;
    nextDocumentExpiry?: Date;
    requestSentDate?: Date;
    requestSentCount?: number;
  };
};

const isRequestSentStatus = (status: StatusType): boolean => {
  return (
    status === RefreshStatus.RequestSent ||
    status === RefreshStatus.RequestSentOverdue ||
    status === DocumentRefreshStatusEnum.RequestSent ||
    status === DocumentRefreshStatusEnum.RequestSentOverdue
  );
};

const isOverdueStatus = (status: StatusType): boolean => {
  return (
    status === RefreshStatus.RequestSentOverdue ||
    status === DocumentRefreshStatusEnum.RequestSentOverdue
  );
};

const isExpiredStatus = (status: StatusType): boolean => {
  return (
    status === RefreshStatus.RefreshRequired || status === DocumentRefreshStatusEnum.Expired
  );
};

const isExpiringSoonStatus = (status: StatusType): boolean => {
  return (
    status === RefreshStatus.UpcomingRefresh ||
    status === DocumentRefreshStatusEnum.ExpiringSoon
  );
};

export const BaseStatusTooltip = ({ statusType, tooltipData }: BaseStatusTooltipProps) => {
  const now = new Date();

  if (!tooltipData) {
    return null;
  }

  const { nextRefreshDueDate, nextDocumentExpiry, requestSentDate, requestSentCount } =
    tooltipData;

  if (isRequestSentStatus(statusType)) {
    if (!requestSentDate) {
      return null;
    }

    const isOverdue = isOverdueStatus(statusType);
    const minutesSince = getTimeSinceInMinutes(requestSentDate);
    const hoursSince = getTimeSinceInHours(requestSentDate);

    if (!isOverdue && minutesSince < 1) {
      return <Text className={tooltipText}>Just now</Text>;
    }

    if (!isOverdue && hoursSince < 24) {
      return <Text className={tooltipText}>Sent today</Text>;
    }

    const daysSince = getDaysDifference(requestSentDate, now);
    const requestCount = requestSentCount ?? 1;

    const rows: { label: string; value: string }[] = [
      { label: "Requests sent", value: String(requestCount) },
      { label: "Last requested", value: `${formatCountText(daysSince, "day")} ago` },
    ];

    return (
      <table className={tooltipTableStyle}>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              <td className={tooltipLabelStyle}>{row.label}:</td>
              <td>{row.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (isExpiredStatus(statusType)) {
    const expiryDate = nextRefreshDueDate || nextDocumentExpiry;
    if (!expiryDate) {
      return null;
    }

    const daysSince = getDaysDifference(expiryDate, now);
    return (
      <Flex alignItems="center" flexDirection="row" gap={12} className={expiredContainer}>
        <Text className={tooltipText}>Expired:</Text>
        <Flex alignItems="center" gap={4} className={expiredIconContainer}>
          <Icon type="exclamation-triangle" variant="outline" color={Colors.Warning500} />
          <Text className={tooltipText}>{formatCountText(daysSince, "day")} ago</Text>
        </Flex>
      </Flex>
    );
  }

  if (isExpiringSoonStatus(statusType)) {
    const expiryDate = nextRefreshDueDate || nextDocumentExpiry;
    if (!expiryDate) {
      return null;
    }

    const nextExpiryDate = new Date(expiryDate);
    if (isSameDay(nextExpiryDate, now)) {
      return <Text className={tooltipText}>Expires today</Text>;
    }

    const daysUntil = getDaysDifference(now, expiryDate);
    return (
      <Flex alignItems="center" flexDirection="row" gap={24} className={expiresInContainer}>
        <Text className={tooltipText}>Expires in:</Text>
        <Text className={tooltipText}>{formatDaysAhead(daysUntil)}</Text>
      </Flex>
    );
  }

  return null;
};
